import React, { Fragment, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Container from '../../../../core/components/atoms/Container';
import BrainLoadingWrapper from '../../../../core/components/molecules/BrainLoading';
import { BreadcrumbsItem } from '../../../../core/components/organism/Breadcrumbs';
import Material from '../../../../core/components/organism/Material';
import NoContent from '../../../../core/components/templates/NoContent';
import { markMaterialView } from '../../../../core/queries/materials';
import { getDisciplineName } from '../../../../core/utils/new_discipline';
import ContentFooter from '../../components/contentFooter';
import { useMaterial } from '../../hooks/useMaterials';
import { useMaterialStore } from '../../stores/materialStore';
import { getModuleLabel } from '../../utils/contentHelper';
import { useAccessControlStore } from '../../../../core/stores/accessControlStore';
import { useMaterialAccessControl } from '../../hooks/useMaterialAccessControl';
import Lock from '../../../../core/components/organism/Lock';
import { validateLoadContent } from '../../../../core/utils/freeValidations';

const MaterialPage = React.memo(function MaterialPage() {
  const navigate = useNavigate();
  const { discipline, module, subject, slug } = useParams();

  const { isFreeUser } = useAccessControlStore();

  const { mutate, isPending, isError } = useMaterial();
  const { mutate: mutateMaterialAccessControl, isSuccess } = useMaterialAccessControl(slug || '');

  const {
    allMaterials,
    material,
    nextLink,
    previousLink,
    currentMaterialIndex,
    setMaterial,
    setNextAndPreviousMaterial,
  } = useMaterialStore();

  useEffect(() => {
    if(!validateLoadContent(slug || '')) {
      return;
    }

    if (module) {
      mutate(module);
    }
  }, []);

  if (!validateLoadContent(slug || '')) {
    return <Lock />;
  }

  useEffect(() => {
    if(allMaterials.length > 0 && slug && !isFreeUser) {
      setMaterial(slug);
    }

    if(allMaterials.length > 0 && slug && isFreeUser) {
      mutateMaterialAccessControl();
    }
  }, [allMaterials])

  useEffect(() => {
    if(allMaterials.length > 0 && slug && !isFreeUser) {
      markMaterialView(slug);
      setMaterial(slug);
    }

    if(allMaterials.length > 0 && slug && isFreeUser) {
      mutateMaterialAccessControl();
    }
  }, [slug])

  useEffect(() => {
    if(isSuccess && isFreeUser) {
      markMaterialView(slug || '');
      setMaterial(slug || '');
    }
  }, [isSuccess])

  useEffect(() => {
    if(material) {
      dispatchSetContentAsDoneEvent();
    }
  }, [material])

  function dispatchSetContentAsDoneEvent() {
    const setContentAsDone = new CustomEvent('setContentAsDone', {
      detail: {
        contentId: material.id,
      },
    });
    
    window.dispatchEvent(setContentAsDone);
  }

  useEffect(() => {
    const baseLink = `/disciplinas/${discipline}/modulo/${module}/assunto/${subject}/material`;
    setNextAndPreviousMaterial(baseLink);
  }, [currentMaterialIndex]);

  if (isPending || !material) {
    return (
      <BrainLoadingWrapper />
    );
  }

  const nextMaterial = () => {
    if (nextLink) {
      navigate(nextLink);
    }
  };

  const previousMaterial = () => {
    if (previousLink) {
      navigate(previousLink);
    }
  };

  if(isError){
    const url = `/disciplinas/${discipline}/modulo/${module}/tipo/materiais`;

    return <NoContent url={url}/>
  }

  const breadcrumbItems: BreadcrumbsItem[] = [
    {
      label: discipline ? getDisciplineName(discipline).replace('Humana', '') : '',
      href: `/disciplinas/${discipline}`,
    },
    {
      label: getModuleLabel(module || ''),
      href: `/disciplinas/${discipline}/modulo/${module}/tipo/materiais`,
    },
    {
      label: material?.title || '',
      href: '#',
    },
  ];

  return (
    <Fragment>
      <Container>
        <div className='mb-16'>
          <Material
            review={material.review}
            isFavorite={material.favorite}
            title={material.title}
            breadcrumb={breadcrumbItems}
            materialType={material.file_category}
            materialId={material.id}
            materialSlug={material.slug}
            file={material.uri}
          />
        </div>
      </Container>
      <ContentFooter
        contentModalInitialTab={2}
        contentModalSubtitle='Lista de materiais'
        previousLabel='Material anterior'
        nextLabel='Próximo material'
        nextContent={nextMaterial}
        previousContent={previousMaterial}
        currentIndex={currentMaterialIndex || 0}
        contentLength={allMaterials.length || 0}
        contentType="materiais"
      />
    </Fragment>
  );
});

export default MaterialPage;
